<script setup lang="ts">
import ContextMenu from '@/features/ContextMenu/index';
import ArticleCommentReply from '@/pages/journalArticle/ui/ArticleCommentReply.vue';

import { modifyDate } from '@/shared/utils/modifyTime';
import { buildFullName } from '@/shared/utils/buildFullName';
import { useModalStore } from '@/shared/ui/AppModal/store/modal';
import { useUserStore } from '@/entities/user/store';

import type { ArticleCommentView } from '@/pages/journalArticle/model/types/articleComments';

const props = defineProps<{
  comment: ArticleCommentView;
  articleAuthorId?: number;
}>();

const emit = defineEmits<{
  (e: 'send', text: string): void;
}>();

const userStore = useUserStore();
const modalStore = useModalStore();
const { user, isAuth } = storeToRefs(userStore);

const isMobile: boolean = useIsMobile();
const isShowFull: Ref<boolean> = ref(false);
const isShowReplyField: Ref<boolean> = ref(false);
const isShowMoreButton: Ref<boolean> = ref(false);
const previewEl = ref<HTMLElement | null>(null);

let resizeObserver: ResizeObserver | null = null;

const fullName: ComputedRef<string> = computed(
  () => buildFullName(props.comment.creator) || 'Пользователь',
);

const isShowContextMenu: ComputedRef<boolean> = computed(
  () =>
    isAuth.value &&
    Boolean(user.value?.lawyer) &&
    user.value?.id === props.articleAuthorId &&
    user.value?.id !== props.comment.creator?.id,
);

const isEditReply: ComputedRef<boolean> = computed(() => Boolean(props.comment.review));

const contextButtonText: ComputedRef<string> = computed(() =>
  isEditReply.value ? 'Редактировать' : 'Ответить',
);

const recalcShowMore = (): void => {
  const el = previewEl.value;
  if (!el) {
    isShowMoreButton.value = false;
    return;
  }
  isShowMoreButton.value = el.scrollHeight > el.clientHeight;
};

const showFullText = (): void => {
  isShowFull.value = true;
};

const openReplyField = (close: () => void): void => {
  isShowReplyField.value = true;
  close();
};

const openComplaint = (close: () => void): void => {
  close();
  const creatorId = props.comment.creator?.id;
  if (!creatorId) return;

  modalStore.setModalState('complaint', true, {
    lawyerInfo: { user_id: creatorId },
    isShowReasons: false,
  });
};

const sendReply = (text: string): void => {
  emit('send', text);
};

onMounted(() => {
  if (!previewEl.value) return;

  resizeObserver = new ResizeObserver(recalcShowMore);
  resizeObserver.observe(previewEl.value);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});
</script>

<template>
  <article
    :class="['article-comment-card', { _mobile: isMobile }]"
    itemprop="review"
    itemscope
    itemtype="https://schema.org/Review"
  >
    <div class="article-comment-card__header">
      <div class="article-comment-card__info">
        <div class="article-comment-card__author" itemprop="author">
          {{ fullName }}
        </div>
        <div class="article-comment-card__date">
          Опубликовано:
          <span class="article-comment-card__date-value">
            {{ modifyDate(comment.createdAt) }}
          </span>
          <meta itemprop="datePublished" :content="comment.createdAt" />
        </div>
      </div>
      <div v-if="isShowContextMenu" class="article-comment-card__context">
        <ContextMenu>
          <template #default="{ close }">
            <div class="article-comment-card__context-panel">
              <button
                class="article-comment-card__context-button _reply"
                type="button"
                @click.prevent="openReplyField(close)"
              >
                <AppIcon name="Share" />
                <span class="article-comment-card__context-button-text">
                  {{ contextButtonText }}
                </span>
              </button>
              <button
                class="article-comment-card__context-button"
                aria-label="Пожаловаться"
                type="button"
                @click.prevent="openComplaint(close)"
              >
                <AppIcon name="Alarm" />
                <span class="article-comment-card__context-button-text _danger">
                  Пожаловаться
                </span>
              </button>
            </div>
          </template>
        </ContextMenu>
      </div>
    </div>
    <div class="article-comment-card__body">
      <div class="article-comment-card__preview-wrapper">
        <div
          ref="previewEl"
          :class="['article-comment-card__preview', { _full: isShowFull }]"
          itemprop="reviewBody"
        >
          {{ comment.fullText }}
        </div>
      </div>
      <button
        v-if="isShowMoreButton && !isShowFull"
        class="article-comment-card__button"
        type="button"
        aria-label="Подробнее"
        @click.prevent="showFullText"
      >
        Подробнее
        <AppIcon class="article-comment-card__icon" name="ArrowDownSmall" />
      </button>
    </div>
    <div v-if="isShowReplyField || comment.review" class="article-comment-card__comment">
      <ArticleCommentReply
        :comment="comment"
        :is-show-reply-field="isShowReplyField"
        @send="sendReply"
        @cancel="isShowReplyField = false"
      />
    </div>
  </article>
</template>

<style lang="less">
.article-comment-card {
  .flex-column(@size-x4);

  width: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
  }

  &__author {
    font-size: 14px;
    line-height: 19px;
    font-weight: 600;
    margin-bottom: 2px;
  }

  &__date {
    font-size: @size-x3;
    line-height: @size-x4;
    font-weight: 500;
    color: @color-dove-gray;
  }

  &__date-value {
    color: @color-elephant;
  }

  &__context {
    margin-right: -@size-x2;
    margin-left: 10px;
  }

  &__context-panel {
    min-width: 180px;
    background: #fff;
    border-radius: @main-size;
    box-shadow:
      -@main-size -5px 25px rgba(0, 0, 0, 0.06),
      0 @size-x10 @size-x10 -18px rgba(139, 144, 164, 0.2);
    padding: @size-x4 @size-x4 @size-x4 @size-x3;
  }

  &__context-button {
    .button-reset();

    display: inline-flex;
    align-items: center;
    font-size: 14px;
    line-height: 19px;
    font-weight: 600;

    &._reply {
      padding-bottom: @size-x3;
      margin-bottom: @size-x2;
      min-width: 117px;
      border-bottom: 0.25px solid @color-dove-gray;
    }

    svg {
      width: 13px;
      height: 13px;
    }
  }

  &__context-button-text {
    margin-left: @size-x2;

    &._danger {
      color: @color-crimson;
    }
  }

  &__body {
    position: relative;
  }

  &__preview-wrapper {
    display: flex;
    align-items: flex-end;
    position: relative;
  }

  &__preview {
    font-size: @size-x4;
    line-height: 22px;
    font-weight: 400;
    word-break: break-word;
    max-height: 66px;
    overflow: hidden;

    &._full {
      max-height: 100%;
      overflow: auto;
    }
  }

  &__button {
    .button-reset();

    display: inline-flex;
    align-items: center;
    font-size: @size-x4;
    line-height: 22px;
    font-weight: 400;
    color: @color-silver-chalice;
    position: absolute;
    bottom: 0;
    right: 0;
    background-color: white;
    padding-left: 6px;

    &::before {
      content: '...';
      color: @color-elephant;
      margin-right: 2px;
      box-shadow: 0 20px 12px 23px #fff;
    }

    &:hover {
      color: @color-persimmon;

      .app-icon svg path {
        stroke: @color-persimmon;
      }
    }

    span {
      color: @color-elephant;
    }
  }

  &__icon {
    width: @size-x4;
    height: @size-x4;

    svg path {
      stroke: @color-silver-chalice;
    }
  }

  &__comment {
    position: relative;
    z-index: 2;
  }

  &._mobile &__preview {
    max-height: 176px;

    &._full {
      max-height: 100%;
    }
  }

  &._mobile &__button {
    font-size: 14px;
    height: 15px;
    bottom: 2px;

    &::before {
      box-shadow: 3px 32px 21px 29px #fff;
    }
  }
}
</style>
